import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, setLanguage, t, getLocalizedPath } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: { da: 'Forside', en: 'Home', de: 'Startseite' }, path: '/' },
    { name: { da: 'Service', en: 'Service', de: 'Service' }, path: '/service' },
    { name: { da: 'Teknisk rådgivning', en: 'Technical Advisory', de: 'Technische Beratung' }, path: '/teknisk-radgivning' },
    { name: { da: 'Salg og køb', en: 'Sales & Purchase', de: 'Verkauf & Kauf' }, path: '/salg-og-kob' },
    { name: { da: 'ECM certifikat', en: 'ECM Certificate', de: 'ECM-Zertifikat' }, path: '/ecm-certifikat' },
    { name: { da: 'Kontakt', en: 'Contact', de: 'Kontakt' }, path: '/kontakt' },
  ];

  const languagesList = [
    { code: 'da', label: 'Dansk' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' }
  ];

  const [isLangOpen, setIsLangOpen] = useState(false);

  const toggleLanguage = () => {
    if (language === 'da') setLanguage('en');
    else if (language === 'en') setLanguage('de');
    else setLanguage('da');
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md py-3 border-slate-200 shadow-sm' 
          : 'bg-transparent py-5 border-transparent'
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <Link to={getLocalizedPath('/')} className="flex items-center gap-3 group">
            <img 
              src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/b13cc169-9c47-4994-b3be-a5508b3472d5.png" 
              alt="Rail & Maskinteknik Logo" 
              className="h-[85px] w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={getLocalizedPath(item.path)}
                className={cn(
                  'text-sm font-bold transition-colors uppercase tracking-wider',
                  isScrolled 
                    ? (location.pathname === getLocalizedPath(item.path) ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900')
                    : (location.pathname === getLocalizedPath(item.path) ? 'text-white' : 'text-white/70 hover:text-white')
                )}
              >
                {t(item.name)}
              </Link>
            ))}
            
            {/* Desktop Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider transition-colors",
                  isScrolled 
                    ? "border-slate-200 text-slate-600 hover:bg-slate-50" 
                    : "border-white/30 text-white hover:bg-white/10"
                )}
              >
                <Globe className="w-3.5 h-3.5" />
                {language}
              </button>

              {isLangOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsLangOpen(false)} 
                  />
                  <div className="absolute right-0 mt-2 w-36 bg-white border border-slate-100 rounded-xl shadow-xl py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    {languagesList.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors",
                          language === lang.code
                            ? "bg-slate-50 text-slate-900"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        )}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center gap-4 lg:hidden">
             <button
              onClick={toggleLanguage}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider",
                isScrolled ? "border-slate-200 text-slate-600" : "border-white/30 text-white"
              )}
            >
              {language}
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "p-2 transition-colors",
                isScrolled ? "text-slate-600 hover:text-slate-900" : "text-white hover:text-white/80"
              )}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <div
        className={cn(
          'lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 transition-all duration-300 overflow-hidden',
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <nav className="flex flex-col p-4 gap-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={getLocalizedPath(item.path)}
              onClick={() => setIsOpen(false)}
              className={cn(
                'text-base font-medium py-2 border-b border-slate-50 last:border-0',
                location.pathname === getLocalizedPath(item.path) ? 'text-slate-900' : 'text-slate-600'
              )}
            >
              {t(item.name)}
            </Link>
          ))}

          {/* Mobile Language Selector */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
            {languagesList.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsOpen(false);
                }}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-all",
                  language === lang.code
                    ? "bg-slate-900 border-slate-900 text-white"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                )}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
